import React, { useState, useEffect } from 'react';
import { DocumentArrowDownIcon, ChartBarIcon, CalendarIcon } from '@heroicons/react/24/solid';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import { reports, devices, venues } from '../services/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface ReportData {
  summary: {
    totalMeasurements: number;
    avgLaeq: number;
    maxLaeq: number;
    maxLCpeak: number;
    violationCount: number;
    complianceRate: number;
  };
  hourlyAverages: Array<{
    hour: string;
    avgLaeq: number;
    maxLCpeak: number;
  }>;
  dailyStats: Array<{
    date: string;
    avgLaeq: number;
    maxLaeq: number;
    violations: number;
  }>;
  violations: Array<{
    timestamp: string;
    threshold: string;
    measuredValue: number;
    duration: number;
  }>;
}

const Reports: React.FC = () => {
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [venueList, setVenueList] = useState<any[]>([]);
  const [deviceList, setDeviceList] = useState<any[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    if (selectedVenue) {
      loadDevices(selectedVenue);
    }
  }, [selectedVenue]);

  const loadVenues = async () => {
    try {
      const response = await venues.list();
      setVenueList(response.data);
      if (response.data.length > 0) {
        setSelectedVenue(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load venues', error);
    }
  };

  const loadDevices = async (venueId: string) => {
    try {
      const response = await devices.list(venueId);
      setDeviceList(response.data);
      if (response.data.length > 0) {
        setSelectedDevice(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load devices', error);
    }
  };

  const generateReport = async () => {
    if (!selectedVenue) return;
    
    setLoading(true);
    try {
      const response = await reports.generate(selectedVenue, startDate, endDate);
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'csv' | 'pdf') => {
    if (!selectedVenue) return;
    
    try {
      const response = await reports.export(selectedVenue, format, startDate, endDate);
      
      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-report-${startDate}-to-${endDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report', error);
    }
  };

  const getComplianceColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Compliance Reports</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Generate and export SPL compliance reports</p>
      </div>

      {/* Report Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Venue
            </label>
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {venueList.map(venue => (
                <option key={venue.id} value={venue.id}>{venue.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Device (Optional)
            </label>
            <select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Devices</option>
              {deviceList.map(device => (
                <option key={device.id} value={device.id}>{device.device_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-50 dark:bg-blue-900/200 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>

        {/* Quick Date Ranges */}
        <div className="mt-4 flex space-x-2">
          <button
            onClick={() => {
              setStartDate(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
              setEndDate(format(new Date(), 'yyyy-MM-dd'));
            }}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
          >
            Last 24 Hours
          </button>
          <button
            onClick={() => {
              setStartDate(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
              setEndDate(format(new Date(), 'yyyy-MM-dd'));
            }}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
          >
            Last 7 Days
          </button>
          <button
            onClick={() => {
              setStartDate(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
              setEndDate(format(new Date(), 'yyyy-MM-dd'));
            }}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
          >
            Last 30 Days
          </button>
        </div>
      </div>

      {reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Measurements</div>
              <div className="text-2xl font-bold">{reportData.summary.totalMeasurements.toLocaleString()}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Avg LAeq</div>
              <div className="text-2xl font-bold">{reportData.summary.avgLaeq.toFixed(1)} dB</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Max LAeq</div>
              <div className="text-2xl font-bold">{reportData.summary.maxLaeq.toFixed(1)} dB</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Max LCpeak</div>
              <div className="text-2xl font-bold">{reportData.summary.maxLCpeak.toFixed(1)} dB</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Violations</div>
              <div className="text-2xl font-bold text-red-600">{reportData.summary.violationCount}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Compliance Rate</div>
              <div className={`text-2xl font-bold ${getComplianceColor(reportData.summary.complianceRate)}`}>
                {reportData.summary.complianceRate.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Hourly Averages */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Hourly Average LAeq</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.hourlyAverages}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis domain={[40, 100]} />
                  <Tooltip />
                  <Bar dataKey="avgLaeq" fill="#3B82F6" name="Avg LAeq" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Daily Trends */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Daily Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={reportData.dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => format(new Date(date), 'MM/dd')}
                  />
                  <YAxis domain={[40, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="avgLaeq" stroke="#3B82F6" name="Avg LAeq" />
                  <Line type="monotone" dataKey="maxLaeq" stroke="#EF4444" name="Max LAeq" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Violations Table */}
          {reportData.violations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Threshold Violations</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Threshold
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Measured
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {reportData.violations.slice(0, 10).map((violation, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {format(new Date(violation.timestamp), 'PPp')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {violation.threshold}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                          {violation.measuredValue.toFixed(1)} dB
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {violation.duration}s
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {reportData.violations.length > 10 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 px-6">
                    Showing 10 of {reportData.violations.length} violations
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Export Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => exportReport('csv')}
              className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-900"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => exportReport('pdf')}
              className="flex items-center px-4 py-2 bg-blue-50 dark:bg-blue-900/200 text-white rounded-md hover:bg-blue-600"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Export PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;